import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getPublications, getSubscriberBalance } from '../api/videoService';
import { logoBgColor } from '../utils/logoBg';
import { useAudio } from '../hooks/AudioContext';
import UserAvatar from './UserAvatar';
import { useMsal } from '@azure/msal-react';

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #121212;
  color: white;
  padding: 20px;
`;

const Header = styled.header`
  position: relative;
  text-align: center;
  margin-bottom: 30px;
  padding-top: 20px;
`;

const AvatarWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const BrandLogo = styled.div`
  margin-bottom: 15px;
  display: inline-block;
`;

const BrandImage = styled.img`
  height: 50px;
  width: auto;
  margin-bottom: 5px;
`;

const BrandText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: white;
  letter-spacing: -0.5px;
  text-transform: lowercase;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  opacity: 0.8;
  margin: 0;
`;

const PublicationsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  padding: 20px 0;
  overflow-y: auto;
  max-width: 600px;
  margin: 0 auto;
`;

const PublicationItem = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr auto;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 15px 20px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  ${props => !props.active && `
    opacity: 0.5;
    filter: grayscale(70%);
  `}
`;

const LogoContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  background-color: ${props => logoBgColor(props.$bg)};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  border: 2px solid ${props => props.color || 'rgba(255, 255, 255, 0.3)'};
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 6px;
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.05);
  }
`;

const Logo = styled.img`
  max-width: 80%;
  max-height: 80%;
  object-fit: contain;
`; 

const PublicationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PublicationName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: white;
`;

const PublicationDescription = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const PointsBalance = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const PointsLabel = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PointsValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #4CAF50;
`;



const PublicationSelector = () => {
  const navigate = useNavigate();
  const { enableAudio, autoUnmuteEnabled } = useAudio();
  const { instance, accounts } = useMsal();
  const [publications, setPublications] = useState([]);
  const [balancesByPublication, setBalancesByPublication] = useState({});

  useEffect(() => {
    const account = accounts[0];
    const fetchPubs = async () => {
      let idToken = null;
      if (account) {
        try {
          let result = await instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account });
          if (!result.idToken) {
            result = await instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account, forceRefresh: true });
          }
          idToken = result.idToken || null;
        } catch {
          // proceed without token — only open publications will show
        }
      }
      try {
        const pubs = await getPublications(idToken);
        setPublications(pubs);

        if (idToken && pubs.length > 0) {
          const balances = await Promise.all(
            pubs.map(async (pub) => {
              try {
                const result = await getSubscriberBalance(pub.id, idToken);
                return [pub.id, Number(result.balance) || 0];
              } catch {
                return [pub.id, null];
              }
            })
          );

          setBalancesByPublication(
            balances.reduce((acc, [pubId, balance]) => {
              if (balance !== null) acc[pubId] = balance;
              return acc;
            }, {})
          );
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchPubs();
  }, [instance, accounts]);
  
  const handlePublicationSelect = (publication) => {
    if (publication.active) {
      // Enable audio when a publication is clicked (if feature is enabled)
      if (autoUnmuteEnabled) {
        console.log(`🔊 Enabling audio after user clicked on ${publication.name}`);
        enableAudio();
      }
      
      // Navigate to the video feed for this publication
      navigate(`/feed/${publication.id}`);
    } else {
      // Alert for inactive publications
      alert(`${publication.name} videos coming soon!`);
    }
  };
  
  return (
    <SelectorContainer>
      <Header>
        <AvatarWrapper><UserAvatar /></AvatarWrapper>
        <BrandLogo>
          <BrandImage src="/images/vusub-logo.svg" alt="vusub logo" />
          <BrandText>vusub</BrandText>
        </BrandLogo>
        <Title>Select a Reel</Title>
        <Subtitle>Choose a reel to pay down your balance</Subtitle>
      </Header>
      
      <PublicationsList>
        {publications
          .filter(publication => publication.active)
          .map(publication => (
            <PublicationItem 
              key={publication.id}
              active={publication.active}
              onClick={() => handlePublicationSelect(publication)}
            >
              <LogoContainer color={publication.color} $bg={publication.logo_bg}>
                <Logo src={publication.logo_url} alt={publication.name} />
              </LogoContainer>
              <PublicationInfo>
                <PublicationName>{publication.name}</PublicationName>
                <PublicationDescription>{publication.description}</PublicationDescription>
              </PublicationInfo>
              <PointsBalance>
                <PointsLabel>Points</PointsLabel>
                <PointsValue>{balancesByPublication[publication.id] ?? 0}</PointsValue>
              </PointsBalance>
            </PublicationItem>
          ))
        }
      </PublicationsList>
    </SelectorContainer>
  );
};

export default PublicationSelector;
